/**
 * @module list
 *
 * A set of functions for performing operations on lists (arrays).
 */
import { baseEmployeeAttrIds } from '../config/settings/attribute.settings';

interface LocaleOptions {
  numeric?: boolean;
}

// ------------------------------------------------------------------ Sorting --
/**
 * Takes an array of objects and sorts them by the attribute `orderBy` in the
 * order of `order`.
 *
 * Converts all values to strings and use `String.localeCompare()` to determine
 * if `a` comes before `b`.
 *
 * ref: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/String/localeCompare
 */
const byAttribute =
  (orderBy: string = 'id', order: 'asc' | 'desc' = 'asc', opts?: { numeric?: boolean }) =>
  (a: Record<string, any>, b: Record<string, any>): number => {
    const localeOptions: LocaleOptions = {
      numeric: opts?.numeric,
    };
    const aStr = String(a[orderBy]);
    const bStr = String(b[orderBy]);
    const comparison = aStr.localeCompare(bStr, undefined, localeOptions); // negative, positive, or zero (integer)

    // Flip the sign based on order.
    return order === 'asc' ? comparison : -comparison;
  };

// Sorts an array of objects containing an attribute `name`.
const byName = (a: { name: string }, b: { name: string }): number => a.name.localeCompare(b.name);

// Sorts an array of objects containing an attribute `label`.
const byLabel = (a: { label: string }, b: { label: string }): number => a.label.localeCompare(b.label);

const byIds =
  (ids: string[] | undefined | null) =>
  (el: { id: string }): boolean => {
    if (!ids) return true;

    return ids.includes(el.id);
  };

const byAlphaNum = (a: string, b: string): number => a.localeCompare(b, 'en', { numeric: true });

const onlyUnique = (value: any, index: number, list: any[]): boolean => {
  return list.indexOf(value) === index;
};

// Return only those employee attributes which are people leaders.
const peopleLeader = (employeeAttribute: { baseAttributeId: number }): boolean => {
  return employeeAttribute?.baseAttributeId === baseEmployeeAttrIds.MANAGER_EMPLOYEE_ID;
};

// --------------------------------------------------------------- Inspection --
const hasId = (id: string, list: any[]): boolean => {
  if (!Array.isArray(list) || list.length === 0) return false;

  return list.some((el) => el?.id === id);
};

// Returns `true` if list of objects contains at least one object with an
// attribute `name` and value `value`.
const hasValue = (name: string, value: any, list: any[]): boolean => {
  if (!Array.isArray(list) || list.length === 0) return false;

  return list.some((el) => el[name] === value);
};

// Create a set of `unique` elements from `list` and then filter out all elements
// from `list` which exist in the `unique` set; whatever remains is a duplicate.
const duplicatesFrom = (list: any[]): any[] => {
  if (!Array.isArray(list)) return [];

  const unique = new Set(list);
  const duplicates = list.filter((el) => {
    if (unique.has(el)) {
      unique.delete(el);
      return false;
    } else {
      return true;
    }
  });

  return duplicates;
};

// ----------------------------------------------------------------- Creating --
/**
 * Returns an array with a sequence of integer values ranging from `from` to `to`.
 *
 * @example
 * const list = numberedArrayFrom(4, 10); // [4, 5, 6, 7, 8, 9, 10]
 *
 * @see https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Array/from
 */
const numberedArrayFrom = (start: number, end: number): number[] => {
  if (start > end || start < 0) return [];

  const length = end - start + 1;

  return Array.from(Array(length), (e, i) => i + start);
};

const groupBy = (collection, iteratee = (x) => x) => {
  const it = typeof iteratee === 'function' ? iteratee : ({ [iteratee]: prop }) => prop;
  const list = Array.isArray(collection) ? collection : Object.values(collection);

  return list.reduce((r, e) => {
    const k = it(e);

    r[k] = r[k] || [];
    r[k].push(e);

    return r;
  }, {});
};

export const list = {
  byAlphaNum,
  byAttribute,
  byIds,
  byLabel,
  byName,
  duplicatesFrom,
  groupBy,
  hasId,
  hasValue,
  numberedArrayFrom,
  onlyUnique,
  peopleLeader,
};
